/**
 * Сессия пользователя: `GET /api/me`, вход, выход, роль.
 *
 * Роль приходит из сессии и не переключается в интерфейсе — тумблер «Главврач / Исполнитель»
 * из макета удалён вместе с `.seg.seg-dark` (см. `docs/spec/ui-blocks.md` §1.1).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { setUnauthorizedHandler, type ApiError } from '../api/client';
import { useLogin, useLogout, useMe } from '../api/hooks';
import { setDisplayTimeZone } from '../lib/deadlines';
import type { ClinicRef, MeDto, Role, UserProfile } from '../api/types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
    status: AuthStatus;
    me: MeDto | null;
    user: UserProfile | null;
    clinic: ClinicRef | null;
    role: Role | null;
    isChief: boolean;
    isExecutor: boolean;
    unreadNotifications: number;
    /** Код привязки MAX; `null`, если MAX уже привязан. */
    maxLinkCode: string | null;
    hasRole: (...roles: Role[]) => boolean;
    login: (login: string, password: string) => Promise<MeDto>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
    loginPending: boolean;
    loginError: ApiError | null;
    logoutPending: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [expired, setExpired] = useState(false);

    const meQuery = useMe();
    const loginMutation = useLogin();
    const logoutMutation = useLogout();

    // Любой 401 в любом запросе гасит сессию: дальше RequireRole уводит на /login.
    useEffect(() => {
        setUnauthorizedHandler(() => setExpired(true));
        return () => setUnauthorizedHandler(null);
    }, []);

    useEffect(() => {
        if (expired) qc.clear();
    }, [expired, qc]);

    const me = meQuery.data ?? null;

    // Даты печатаются в таймзоне клиники, а не браузера.
    useEffect(() => {
        setDisplayTimeZone(me?.clinic.timezone);
    }, [me?.clinic.timezone]);

    const login = useCallback(
        async (loginName: string, password: string) => {
            const result = await loginMutation.mutateAsync({ login: loginName, password });
            setExpired(false);
            return result;
        },
        [loginMutation],
    );

    const logout = useCallback(async () => {
        try {
            await logoutMutation.mutateAsync();
        } finally {
            setExpired(true);
            navigate('/login', { replace: true });
        }
    }, [logoutMutation, navigate]);

    const refresh = useCallback(async () => {
        await meQuery.refetch();
    }, [meQuery]);

    const value = useMemo<AuthContextValue>(() => {
        const status: AuthStatus = expired
            ? 'anonymous'
            : meQuery.isPending
              ? 'loading'
              : me
                ? 'authenticated'
                : 'anonymous';
        const role = me?.user.role ?? null;
        return {
            status,
            me,
            user: me?.user ?? null,
            clinic: me?.clinic ?? null,
            role,
            isChief: role === 'chief',
            isExecutor: role === 'executor',
            unreadNotifications: me?.unread_notifications ?? 0,
            maxLinkCode: me?.max_link_code ?? null,
            hasRole: (...roles: Role[]) => (role ? roles.includes(role) : false),
            login,
            logout,
            refresh,
            loginPending: loginMutation.isPending,
            loginError: loginMutation.error ?? null,
            logoutPending: logoutMutation.isPending,
        };
    }, [
        expired,
        me,
        meQuery.isPending,
        login,
        logout,
        refresh,
        loginMutation.isPending,
        loginMutation.error,
        logoutMutation.isPending,
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth вызван вне AuthProvider');
    return ctx;
}

/** Внутри защищённого маршрута пользователь заведомо есть. */
export function useCurrentUser(): UserProfile {
    const { user } = useAuth();
    if (!user) throw new Error('Пользователь не авторизован');
    return user;
}

export function useIsChief(): boolean {
    return useAuth().isChief;
}

export function useIsExecutor(): boolean {
    return useAuth().isExecutor;
}
